<script lang="ts">
	import { pushAll } from '$lib/svelte-utils/utils/collectionUtils';
	import { onMount } from 'svelte';
	import type { DropdownItem, SelectableItem } from './DropdownItem';

	export let testId = '';
	export let options: Array<DropdownItem>;
	export let alignMenuLeft = false;
	export let openOn: 'click' | 'hover' = 'hover';
	export let multiple = false;

	export let dropdownClass = '';
	export let itemContainerClass = '';
	export let itemMenuClass = '';
	export let iconClass = '';

	export let showIcon = false;

	export let selectedItems: SelectableItem[] = multiple
		? (options.filter(
				(option) => !('isDivider' in option) && !('isLabel' in option)
			) as SelectableItem[])
		: [];

	let expanded = false;

	let dropdownElement: HTMLElement;

	onMount(async () => {
		addClosingListener();
	});

	function addClosingListener() {
		const clickHandler = (event: Event) => {
			// dropdownElement may no longer exist, hence we need to check
			if (dropdownElement && !dropdownElement.contains(event.target as Element)) {
				expanded = false;
				window.removeEventListener('click', clickHandler);
			}
		};
		window.addEventListener('click', clickHandler);
	}

	function onClickOnItem(clickedItem: SelectableItem) {
		if (!clickedItem.keepOldValue) {
			if (multiple) {
				if (optionIsActive(clickedItem)) {
					selectedItems = selectedItems.filter((item) => item.value !== clickedItem.value);
				} else {
					selectedItems = pushAll(selectedItems, [clickedItem]);
				}
			} else {
				selectedItems = [clickedItem];
			}
		}
		if ('handler' in clickedItem) {
			clickedItem.handler();
		}
		if (!clickedItem.keepOpen) {
			expanded = false;
		}
	}

	function optionIsActive(option: SelectableItem) {
		return selectedItems.some((item) => item.value === option.value);
	}

	function asSelectable(item: DropdownItem) {
		return item as SelectableItem;
	}
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
	data-testid={testId}
	class="relative z-20 inline-block text-left {dropdownClass}"
	bind:this={dropdownElement}
	on:mouseleave={(e) => {
		if (
			openOn === 'hover' &&
			!dropdownElement.contains(
				// @ts-ignore
				e.relatedTarget
			)
		) {
			expanded = false;
		}
	}}
>
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<div
		class="flex items-center text-center"
		on:mouseenter={() => {
			if (openOn === 'hover') {
				expanded = !expanded;
				if (expanded) {
					addClosingListener();
				}
			}
		}}
		on:click|capture={() => {
			expanded = !expanded;
			if (expanded) {
				addClosingListener();
			}
		}}
	>
		<slot name="label" {selectedItems}>
			<div
				class="theme-input-background focus:shadow-outline-blue inline-flex w-full cursor-auto justify-center px-4 py-2 text-sm leading-5 shadow transition duration-150 ease-in-out focus:outline-none active:bg-gray-50 active:text-gray-800"
				id="options-menu"
				aria-haspopup="true"
				aria-expanded="true"
			>
				{selectedItems.length > 0 ? selectedItems[0].text : 'Select...'}
			</div>
		</slot>

		{#if showIcon}
			<div class="px-1 {iconClass}">
				<svg
					class="-mr-1 ml-2 size-5 opacity-70 transition-transform"
					class:rotate-180={expanded}
					viewBox="0 0 20 20"
					fill="currentColor"
				>
					<path
						fill-rule="evenodd"
						d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0
	010-1.414z"
						clip-rule="evenodd"
					/>
				</svg>
			</div>
		{/if}
	</div>

	<!--
      Dropdown panel, show/hide based on dropdown state.
    -->
	{#if expanded}
		<div
			class="absolute origin-top-right {alignMenuLeft
				? 'left-0'
				: 'right-0'} w-max rounded-md shadow-lg"
		>
			<div class="shadow-xs rounded-md bg-white dark:bg-[#111]">
				<div
					class="overflow-y-auto overflow-x-hidden py-2 {itemMenuClass}"
					style="max-height: 400px"
					role="menu"
					aria-orientation="vertical"
					aria-labelledby="options-menu"
				>
					{#each options.filter((option) => !option.hidden) as option}
						{#if 'isLabel' in option}
							<div
								data-testid={option.testId}
								class="px-4 py-2 text-xs text-gray-700 dark:text-gray-300"
							>
								{option.text}
							</div>
						{:else if 'isDivider' in option}
							<div data-testid={option.testId} class="my-0.5 h-px bg-divider" />
						{:else}
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<div
								data-testid={option.testId}
								class="flex items-center {itemContainerClass} {option.disabled ? 'opacity-60' : ''}"
								on:click={() => !option.disabled && onClickOnItem(option)}
							>
								{#if multiple}
									{#key selectedItems.length}
										<input type="checkbox" checked={optionIsActive(option)} />
									{/key}
								{/if}
								<slot name="item" option={asSelectable(option)} {selectedItems} {onClickOnItem}>
									<svelte:element
										this={'href' in option ? 'a' : 'div'}
										class="whitespace-no-wrap block w-full cursor-pointer px-4 py-2 text-sm leading-5 text-black hover:bg-gray-100 focus:outline-none dark:text-white hover:dark:bg-[#222]"
										target={'target' in option ? option.target : ''}
										on:click|stopPropagation={(
											// @ts-ignore
											e
										) => {
											if (option.disabled) {
												e.preventDefault();
											} else {
												onClickOnItem(option);
											}
										}}
										href={'href' in option ? option.href : ''}
									>
										{option.text}
										{#if option.shortcut}
											<span class="shortcut ml-6 opacity-75">{option.shortcut}</span>
										{/if}
									</svelte:element>
								</slot>
							</div>
						{/if}
					{/each}
				</div>
			</div>
		</div>
	{/if}
</div>

<style lang="less">
	input {
		@apply pl-4;
	}
</style>
